import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Download, OpenInNew, Publish, Unpublished } from '@mui/icons-material';
import { IDocKey } from '../components/common/PageHint';
import { DashboardLayout } from '../components/DashboardLayout';
import { PageHint } from '../components/common/PageHint';
import { PageCard } from '../components/common/PageCard';
import { PageToolbar } from '../components/common/PageToolbar';
import { ConfirmationDialog } from '../components/common/ConfirmationDialog';
import { UserAvatar } from '../components/common/UserAvatar';
import { toastService } from '../services/toast.service';
import { getErrorMessage } from '../utils/api-response.util';
import { questionnairesConstructionService, QuestionnairesConstructionService } from '../services/questionnaires-construction/questionnaires-construction.service';
import { ConstructionTemplate, CreateTemplateDto } from '../types/questionnaire-construction';
import { TemplateFormDialog } from '../components/quiz-builder/TemplateFormDialog';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';

type OwnerFilter = 'all' | 'system' | 'own';
type StatusFilter = 'all' | 'draft' | 'published';

export interface QuizBuilderListContentProps {
  service: QuestionnairesConstructionService;
  /** Куди вести при відкритті шаблону, без id в кінці, напр. "/quiz-builder" чи "/admin/quiz-builder". */
  editBasePath: string;
  iDoc: IDocKey;
  /** Адмін бачить і видаляє абсолютно всі шаблони, включно з системними. */
  isAdminMode?: boolean;
}

export const QuizBuilderListContent: React.FC<QuizBuilderListContentProps> = ({
  service,
  editBasePath,
  iDoc,
  isAdminMode = false,
}) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ConstructionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ConstructionTemplate | null>(null);
  const [cloningId, setCloningId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [togglingStatusId, setTogglingStatusId] = useState<number | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await service.getTemplates();
      setTemplates(data);
    } catch {
      toastService.error('Не вдалося завантажити опитувальники');
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleCreate = async (dto: CreateTemplateDto) => {
    try {
      const created = await service.createTemplate(dto);
      toastService.success('Опитувальник створено');
      setCreateOpen(false);
      navigate(`${editBasePath}/${created.id}`);
    } catch {
      toastService.error('Не вдалося створити опитувальник');
    }
  };

  const handleClone = async (template: ConstructionTemplate) => {
    setCloningId(template.id);
    try {
      const created = await service.cloneTemplate(template.id);
      toastService.success('Опитувальник склоновано — можете редагувати копію');
      navigate(`${editBasePath}/${created.id}`);
    } catch {
      toastService.error('Не вдалося склонувати опитувальник');
    } finally {
      setCloningId(null);
    }
  };

  const handleDownloadPdf = async (template: ConstructionTemplate) => {
    setDownloadingId(template.id);
    try {
      await service.downloadBlankFormPdf(template.id, template.nestNameI18n?.uk ?? '');
    } catch {
      toastService.error('Не вдалося завантажити PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleToggleStatus = async (template: ConstructionTemplate) => {
    const nextStatus = template.status === 'published' ? 'draft' : 'published';
    setTogglingStatusId(template.id);
    try {
      await service.updateTemplate(template.id, { status: nextStatus });
      toastService.success(nextStatus === 'published' ? 'Опитувальник опубліковано' : 'Опитувальник переведено в чернетку');
      fetchTemplates();
    } catch (err) {
      toastService.error(getErrorMessage(err, 'Не вдалося змінити статус опитувальника'));
    } finally {
      setTogglingStatusId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const { softDeleted } = await service.deleteTemplate(deleteTarget.id);
      toastService.success(
        softDeleted
          ? 'Опитувальник приховано — його вже проходили клієнти, тож відповіді залишаються збереженими'
          : 'Опитувальник видалено',
      );
      setDeleteTarget(null);
      fetchTemplates();
    } catch {
      toastService.error('Не вдалося видалити опитувальник');
    }
  };

  const visibleTemplates = templates.filter(t => {
    if (ownerFilter === 'system' && !t.isSystem) return false;
    if (ownerFilter === 'own' && t.isSystem) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (search && !(t.nestNameI18n?.uk ?? '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <Box data-i-doc={iDoc}>
      <PageHint iDoc={iDoc} />

      <PageCard>
        <PageToolbar
          storageKey={isAdminMode ? 'filters:admin-quiz-builder' : 'filters:quiz-builder'}
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за назвою',
              value: search,
              onChange: setSearch,
            },
            {
              id: 'owner',
              type: 'select',
              label: 'Власник',
              value: ownerFilter,
              onChange: (v) => setOwnerFilter(v as OwnerFilter),
              options: [
                { value: 'all', label: 'Усі' },
                { value: 'system', label: 'Системні' },
                { value: 'own', label: isAdminMode ? 'Експертів' : 'Мої' },
              ],
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: statusFilter,
              onChange: (v) => setStatusFilter(v as StatusFilter),
              options: [
                { value: 'all', label: 'Усі' },
                { value: 'published', label: 'Готово' },
                { value: 'draft', label: 'Чернетка' },
              ],
            },
          ]}
          onReset={() => { setSearch(''); setOwnerFilter('all'); setStatusFilter('all'); }}
          extraActions={
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
              Створити
            </Button>
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleTemplates.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }} color="text.secondary">
            Опитувальників не знайдено.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Опис</TableCell>
                  <TableCell>Власник</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Відповідей</TableCell>
                  <TableCell>Оновлено</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTemplates.map((t) => (
                  <TableRow
                    key={t.id}
                    hover
                    onClick={() => navigate(`${editBasePath}/${t.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{t.nestNameI18n?.uk || `Опитувальник #${t.id}`}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 320 }}>
                        {t.nestDescriptionI18n?.uk || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {t.isSystem ? (
                        <Chip size="small" label="Системний" color="default" variant="outlined" />
                      ) : isAdminMode ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <UserAvatar path={t.expertAvatar} name={t.expertName ?? undefined} sx={{ width: 24, height: 24, fontSize: 12 }} />
                          <Typography variant="body2">{t.expertName ?? `Експерт #${t.expertId}`}</Typography>
                        </Box>
                      ) : (
                        <Chip size="small" label="Мій" color="primary" variant="filled" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.status === 'published' ? 'Готово' : 'Чернетка'}
                        color={t.status === 'published' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t.isSystem ? 'Серед ваших клієнтів' : ''}>
                        <Typography variant="body2" color={t.surveysCount > 0 ? 'text.primary' : 'text.disabled'}>
                          {t.surveysCount}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{new Date(t.updatedAt).toLocaleDateString('uk-UA')}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Відкрити">
                        <IconButton size="small" onClick={() => navigate(`${editBasePath}/${t.id}`)}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Клонувати собі (можна буде редагувати)">
                        <span>
                          <IconButton size="small" onClick={() => handleClone(t)} disabled={cloningId === t.id}>
                            {cloningId === t.id ? <CircularProgress size={16} /> : <ContentCopy fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Завантажити порожній бланк (PDF)">
                        <span>
                          <IconButton size="small" onClick={() => handleDownloadPdf(t)} disabled={downloadingId === t.id}>
                            {downloadingId === t.id ? <CircularProgress size={16} /> : <Download fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {(isAdminMode || !t.isSystem) && (
                        <Tooltip title={t.status === 'published' ? 'Перевести в чернетку' : 'Опублікувати (стане доступним для поширення)'}>
                          <span>
                            <IconButton size="small" onClick={() => handleToggleStatus(t)} disabled={togglingStatusId === t.id}>
                              {togglingStatusId === t.id ? (
                                <CircularProgress size={16} />
                              ) : t.status === 'published' ? (
                                <Unpublished fontSize="small" />
                              ) : (
                                <Publish fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {(isAdminMode || !t.isSystem) && (
                        <Tooltip title="Видалити">
                          <IconButton size="small" onClick={() => setDeleteTarget(t)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageCard>

      <TemplateFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
      />

      <ConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Видалити опитувальник"
        message={`Видалити «${deleteTarget?.nestNameI18n?.uk}»? Усі кроки, питання й варіанти відповідей також буде видалено. Скасувати дію буде неможливо.`}
        confirmText="Видалити"
      />
    </Box>
  );
};

const QuizBuilderPage: React.FC = () => (
  <DashboardLayout>
    <QuizBuilderListContent
      service={questionnairesConstructionService}
      editBasePath="/quiz-builder"
      iDoc={PageIndices.QUIZ_BUILDER}
    />
  </DashboardLayout>
);

export default QuizBuilderPage;
